import { z } from 'zod';
import { IdSchema, IsoDateSchema, formatIssues, type Character, type Scene } from '../../domain/schemas';
import { createId, nowIso } from '../../lib/ids';
import { MotionError, bindTargets, parsePlan } from './compiler';
import { MotionPlanSchema, SlotNameSchema, type Binding, type MotionPlan, type MotionPlanInput } from './spec';

/**
 * Reusable motion recipes (Phase 3C): a saved motion plan plus a name and format version.
 * Recipes live in the browser's IndexedDB (never uploaded) and can be exported and
 * imported as JSON files.
 *
 * Portability: a recipe's steps refer to target *slots* ("hero", "sign"), never to object
 * ids, so it can be retargeted to any compatible objects in any project. A recipe saved as
 * `project`-local additionally remembers the objects it was bound to (`bindings`) and the
 * project it belongs to; it is clearly marked as such and its bindings are only used in that
 * project. Recipes are independent of projects: deleting a recipe never affects motion that
 * was already applied (that motion is plain keyframes).
 */
export const RECIPE_FORMAT = 'sonicforge-motion-recipe';
export const RECIPE_FILE_FORMAT = 'sonicforge-motion-recipes';
export const RECIPE_VERSION = 1 as const;
export const MAX_RECIPES = 200;
export const MAX_RECIPE_FILE_BYTES = 2 * 1024 * 1024;

export const RecipeSchema = z
  .object({
    format: z.literal(RECIPE_FORMAT),
    version: z.literal(RECIPE_VERSION),
    id: IdSchema,
    name: z.string().trim().min(1, 'A recipe needs a name').max(60),
    description: z.string().max(300).default(''),
    plan: MotionPlanSchema,
    scope: z.enum(['portable', 'project']),
    projectId: IdSchema.nullable().default(null),
    bindings: z.record(SlotNameSchema, IdSchema).nullable().default(null),
    createdAt: IsoDateSchema,
    updatedAt: IsoDateSchema,
  })
  .superRefine((r, ctx) => {
    if (r.scope === 'portable' && (r.projectId !== null || r.bindings !== null)) {
      ctx.addIssue({ code: 'custom', path: ['bindings'], message: 'A portable recipe cannot contain object ids (save it as project-local instead)' });
    }
    if (r.scope === 'project' && (r.projectId === null || r.bindings === null)) {
      ctx.addIssue({ code: 'custom', path: ['bindings'], message: 'A project-local recipe needs its project and bindings' });
    }
  });
export type MotionRecipe = z.infer<typeof RecipeSchema>;

const RecipeFileSchema = z.object({
  format: z.literal(RECIPE_FILE_FORMAT),
  version: z.number().int().min(1),
  recipes: z.array(z.unknown()).max(MAX_RECIPES),
});

export interface RecipeOptions {
  scope?: 'portable' | 'project';
  projectId?: string;
  binding?: Binding;
  description?: string;
  id?: string;
  now?: string;
}

/** Saves a plan as a recipe. Portable recipes keep no object ids. */
export function createRecipe(plan: MotionPlanInput, name: string, options: RecipeOptions = {}): MotionRecipe {
  const parsed = parsePlan(plan);
  const scope = options.scope ?? 'portable';
  const now = options.now ?? nowIso();
  const used = new Set(parsed.steps.flatMap((s) => s.targets));
  const bindings = scope === 'project' ? Object.fromEntries(Object.entries(options.binding ?? {}).filter(([slot]) => used.has(slot))) : null;
  return validateRecipe({
    format: RECIPE_FORMAT,
    version: RECIPE_VERSION,
    id: options.id ?? createId('rcp'),
    name,
    description: options.description ?? '',
    plan: parsed,
    scope,
    projectId: scope === 'project' ? (options.projectId ?? null) : null,
    bindings,
    createdAt: now,
    updatedAt: now,
  });
}

export function validateRecipe(raw: unknown): MotionRecipe {
  const parsed = RecipeSchema.safeParse(raw);
  if (!parsed.success) {
    const issues = formatIssues(parsed.error);
    throw new MotionError(`That recipe is invalid: ${issues[0] ?? 'unknown problem'}`, issues);
  }
  return parsed.data;
}

export const renameRecipe = (r: MotionRecipe, name: string, now = nowIso()): MotionRecipe => validateRecipe({ ...r, name: name.trim(), updatedAt: now });

export const duplicateRecipe = (r: MotionRecipe, id = createId('rcp'), now = nowIso()): MotionRecipe =>
  validateRecipe({ ...structuredClone(r), id, name: `${r.name} copy`.slice(0, 60), createdAt: now, updatedAt: now });

/** A project-local recipe turned portable (bindings dropped). */
export const portableCopy = (r: MotionRecipe, id = createId('rcp'), now = nowIso()): MotionRecipe =>
  validateRecipe({ ...structuredClone(r), id, scope: 'portable', projectId: null, bindings: null, name: `${r.name} (portable)`.slice(0, 60), createdAt: now, updatedAt: now });

/**
 * Proposes a binding for a recipe in a scene. Project-local recipes reuse their bindings in
 * their own project (when those objects still exist); otherwise each slot takes, in order,
 * an unused selected object of a compatible kind, then the only compatible object in the
 * scene. Slots that stay ambiguous are left for the user.
 */
export function suggestBinding(recipe: Pick<MotionRecipe, 'plan' | 'scope' | 'projectId' | 'bindings'>, scene: Scene, projectId: string, selectedIds: readonly string[] = []): Binding {
  const out: Binding = {};
  const taken = new Set<string>();
  const fits = (kind: 'character' | 'object', id: string) => {
    const o = scene.objects.find((x) => x.id === id);
    return !!o && (kind === 'object' || o.type === 'character');
  };
  for (const slot of recipe.plan.slots) {
    const remembered = recipe.scope === 'project' && recipe.projectId === projectId ? recipe.bindings?.[slot.name] : undefined;
    let id = remembered && fits(slot.kind, remembered) && !taken.has(remembered) ? remembered : undefined;
    id ??= selectedIds.find((s) => !taken.has(s) && fits(slot.kind, s));
    if (!id) {
      const candidates = scene.objects.filter((o) => !taken.has(o.id) && fits(slot.kind, o.id));
      if (slot.kind === 'character' && candidates.length === 1) id = candidates[0]!.id;
    }
    if (id) {
      out[slot.name] = id;
      taken.add(id);
    }
  }
  return out;
}

/**
 * Retargets a recipe to objects of a scene. Throws MotionError explaining every
 * incompatibility (a character slot bound to a non-character, a character-only motion on an
 * object, a locked or missing object). Returns the plan and binding to compile.
 */
export function retargetRecipe(recipe: MotionRecipe, binding: Binding, scene: Scene, characters: readonly Character[]): { plan: MotionPlan; binding: Binding } {
  const plan = parsePlan(recipe.plan);
  bindTargets(plan, scene, characters, binding);
  return { plan, binding: { ...binding } };
}

/** Serialises recipes for download. */
export function serializeRecipes(recipes: readonly MotionRecipe[]): string {
  return JSON.stringify({ format: RECIPE_FILE_FORMAT, version: RECIPE_VERSION, recipes }, null, 2);
}

/**
 * Parses a recipe file (several recipes) or a single exported recipe. Every recipe is
 * validated; ids already in `existingIds` get fresh ids so an import never overwrites.
 */
export function parseRecipeFile(text: string, existingIds: ReadonlySet<string> = new Set(), newId: () => string = () => createId('rcp')): MotionRecipe[] {
  if (text.length > MAX_RECIPE_FILE_BYTES) throw new MotionError('The recipe file is too large.');
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new MotionError('The file is not valid JSON.');
  }
  const single = RecipeSchema.safeParse(json);
  let raw: unknown[];
  if (single.success) raw = [json];
  else {
    const file = RecipeFileSchema.safeParse(json);
    if (!file.success) throw new MotionError('This is not a SonicForge motion recipe file.', formatIssues(file.error));
    if (file.data.version > RECIPE_VERSION) throw new MotionError(`The file uses recipe format v${file.data.version}; this build supports up to v${RECIPE_VERSION}.`);
    raw = file.data.recipes;
  }
  const seen = new Set(existingIds);
  return raw.map((r, i) => {
    let recipe: MotionRecipe;
    try {
      recipe = validateRecipe(r);
    } catch (e) {
      const issues = e instanceof MotionError ? e.issues : [String(e)];
      throw new MotionError(`Recipe ${i + 1} is invalid: ${issues[0]}`, issues);
    }
    if (seen.has(recipe.id)) recipe = { ...recipe, id: newId() };
    seen.add(recipe.id);
    return recipe;
  });
}

export const recipeFileName = (recipes: readonly MotionRecipe[]): string =>
  `${recipes.length === 1 ? recipes[0]!.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'recipe' : 'motion-recipes'}.sfrecipe.json`;
